import React, { useMemo, useState } from 'react';
import { SUBSCRIPTION_PLANS } from '../../../utils/stripe';

const PLAN_FEATURES = [
  'Launch-ready seller dashboard & order tools',
  'Stripe payments with Apple Pay & Google Pay',
  '30-day free trial, cancel any time',
  'Compliance guidance and onboarding support',
  'Buyer-facing shopfront with storytelling prompts',
];

const StepSubscriptionCommitment = ({
  formData,
  updateFormData,
  createCheckoutSession,
  createStripeConnectAccount,
  willingToImprove,
  errors,
  setStatusMessage,
}) => {
  const [selectedBilling, setSelectedBilling] = useState('monthly');
  const [busyAction, setBusyAction] = useState(null);

  const planDetails = useMemo(() => {
    const starterPlan = SUBSCRIPTION_PLANS.starter || {};
    const billingKey = selectedBilling === 'annual' ? 'annual' : 'monthly';
    const pricing = starterPlan[billingKey] || starterPlan.monthly || {};

    // Match pricing from NewKinnSellerPage
    const displayAmount = billingKey === 'annual' ? '£8.25/month' : '£15/month';
    const billedAmount = billingKey === 'annual' ? '£99 billed yearly' : 'Billed monthly';

    return {
      name: 'Kinn Starter',
      billingKey,
      pricing: {
        ...pricing,
        displayAmount,
        billedAmount,
      },
      trialDays: 30,
    };
  }, [selectedBilling]);

  const isPriceConfigured = Boolean(planDetails.pricing.priceId);
  const isKycVerified = formData.kycStatus === 'verified';
  const hasStripeAccount = Boolean(formData.stripeAccountId);
  const hasActiveSubscription = ['trialing', 'active'].includes(
    formData.subscriptionStatus
  );

  const handleStripeConnect = async () => {
    setBusyAction('connect');
    try {
      const result = await createStripeConnectAccount({
        businessName:
          formData.fhrsBusinessName ||
          formData.displayName ||
          formData.shopName ||
          'Kinn Seller',
      });
      if (result.success) {
        updateFormData({
          stripeAccountId: result.data.accountId,
          kycStatus: 'pending',
        });
        setStatusMessage?.(
          'Stripe Connect onboarding opened in a new tab. Complete the form to verify payouts.'
        );
        const redirectUrl = result.data.url || result.data.accountLinkUrl;
        if (redirectUrl) {
          window.location.href = redirectUrl;
        }
      } else {
        window.alert(result.error || 'Unable to start Stripe Connect onboarding.');
      }
    } catch (error) {
      window.alert(error.message || 'Stripe Connect onboarding failed.');
    } finally {
      setBusyAction(null);
    }
  };

  const handleCheckout = async () => {
    if (!formData.stripeAccountId) {
      window.alert('Complete Stripe Connect before starting your subscription.');
      return;
    }

    if (willingToImprove === false) {
      window.alert(
        'We can only activate your subscription if you’re willing to work on hygiene improvements.'
      );
      return;
    }

    if (!isPriceConfigured) {
      window.alert(
        'Stripe price IDs are not configured. Please add REACT_APP_STRIPE_PRICE_STARTER_MONTHLY / ANNUAL to your environment.'
      );
      return;
    }

    setBusyAction('checkout');
    try {
      const result = await createCheckoutSession('starter', selectedBilling);
      if (result.success && result.data?.url) {
        updateFormData({ subscriptionStatus: 'pending' });
        setStatusMessage?.('Redirecting you to Stripe Checkout to start your free trial.');
        window.location.href = result.data.url;
      } else {
        window.alert(result.error || 'Unable to start Stripe Checkout.');
      }
    } catch (error) {
      window.alert(error.message || 'Stripe Checkout failed. Please retry.');
    } finally {
      setBusyAction(null);
    }
  };

  const billingLabel =
    planDetails.billingKey === 'annual' ? 'Billed annually' : 'Billed monthly';

  return (
    <div className="space-y-6">
      <div className="bg-white border border-[#F0F0F0] rounded-[20px] p-6 space-y-4">
        <h4 className="text-body-lg font-semibold text-text-primary">
          Payment setup (Stripe Connect)
        </h4>
        {!hasStripeAccount ? (
          <div className="space-y-3">
            <p className="text-body-sm text-text-primary-secondary">
              We use Stripe Connect to verify payouts and keep your funds safe. It only takes a few minutes and you can save your progress if you need documents.
            </p>
            <button
              type="button"
              className="w-full md:w-auto text-center py-3 px-6 rounded-xl font-semibold text-white bg-gradient-to-r from-aurora-primary to-accent-blue-dark hover:shadow-xl hover:scale-105 transition-all duration-300"
              onClick={handleStripeConnect}
              disabled={busyAction === 'connect'}
            >
              {busyAction === 'connect' ? 'Opening Stripe…' : 'Launch Stripe Connect'}
            </button>
          </div>
        ) : (
          <div className="space-y-3">
            <div
              className={`p-4 rounded-[16px] border ${
                isKycVerified
                  ? 'bg-green-50 border-green-200 text-green-800'
                  : 'bg-yellow-50 border-yellow-200 text-yellow-800'
              }`}
            >
              <p className="text-body-sm font-medium">
                Stripe Connect status:{' '}
                {isKycVerified ? 'Verified – payouts ready' : 'Verification in progress'}
              </p>
              {!isKycVerified && (
                <p className="text-body-sm mt-1">
                  Complete the verification steps sent by Stripe to enable payouts. You can re-open onboarding if you need to finish it.
                </p>
              )}
            </div>
            {!isKycVerified && (
              <button
                type="button"
                className="btn-secondary w-full md:w-auto"
                onClick={handleStripeConnect}
                disabled={busyAction === 'connect'}
              >
                {busyAction === 'connect' ? 'Re-opening…' : 'Resume Stripe onboarding'}
              </button>
            )}
          </div>
        )}
        {errors.kyc && (
          <p className="text-body-sm text-red-600">{errors.kyc}</p>
        )}
      </div>

      {isKycVerified && (
        <div className="bg-white border border-[#F0F0F0] rounded-[20px] p-6 space-y-5">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h4 className="text-body-lg font-semibold text-text-primary">
                {planDetails.name}
              </h4>
              <p className="text-body-sm text-text-primary-secondary">
                One simple plan powering your launch, with a {planDetails.trialDays}-day free trial.
              </p>
            </div>
            <div className="text-right">
              <p className="text-h3 font-semibold bg-gradient-to-r from-aurora-primary to-accent-blue-dark bg-clip-text text-transparent">
                {planDetails.pricing.displayAmount || '£—'}
              </p>
              <p className="text-body-xs text-text-primary-secondary uppercase tracking-wide">
                {planDetails.pricing.billedAmount || billingLabel}
              </p>
            </div>
          </div>

          <ul className="space-y-2">
            {PLAN_FEATURES.map((feature) => (
              <li key={feature} className="flex items-start gap-2">
                <span className="w-5 h-5 rounded-full bg-gradient-to-r from-aurora-primary to-accent-blue-dark flex items-center justify-center text-white text-xs font-bold flex-shrink-0">
                  ✓
                </span>
                <span className="text-body-sm text-text-primary/80">{feature}</span>
              </li>
            ))}
          </ul>

          <div className="space-y-3">
            <p className="text-body-sm font-medium text-text-primary">Billing frequency</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                type="button"
                className={`p-4 rounded-[16px] border-2 transition-all ${
                  selectedBilling === 'monthly'
                    ? 'border-accent-blue bg-aurora-bg-secondary'
                    : 'border-shell-border hover:border-accent-blue'
                }`}
                onClick={() => setSelectedBilling('monthly')}
              >
                <span className="block text-body-md font-semibold text-text-primary">
                  Monthly — £15.00
                </span>
                <span className="block text-body-xs text-text-primary-secondary">
                  Cancel any time
                </span>
              </button>
              <button
                type="button"
                className={`p-4 rounded-[16px] border-2 transition-all ${
                  selectedBilling === 'annual'
                    ? 'border-accent-blue bg-aurora-bg-secondary'
                    : 'border-shell-border hover:border-accent-blue'
                }`}
                onClick={() => setSelectedBilling('annual')}
              >
                <span className="block text-body-md font-semibold text-text-primary">
                  Annual — £99.00
                </span>
                <span className="block text-body-xs text-text-primary-secondary">
                  Equivalent to £8.25/month
                </span>
              </button>
            </div>
          </div>

          <div className="space-y-2">
            <button
              type="button"
              className="w-full md:w-auto text-center py-4 px-8 rounded-xl font-semibold text-white bg-gradient-to-r from-aurora-primary to-accent-blue-dark hover:shadow-xl hover:scale-105 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
              onClick={handleCheckout}
              disabled={busyAction === 'checkout' || !isPriceConfigured}
            >
              {busyAction === 'checkout'
                ? 'Redirecting to Stripe…'
                : `Start ${planDetails.trialDays}-day free trial`}
            </button>
            {!isPriceConfigured && (
              <p className="text-body-xs text-red-600">
                Set REACT_APP_STRIPE_PRICE_STARTER_MONTHLY / ANNUAL to enable checkout.
              </p>
            )}
            {errors.subscription && (
              <p className="text-body-sm text-red-600">{errors.subscription}</p>
            )}
          </div>
        </div>
      )}

      <div className="bg-[#FAFAFA] border border-[#F0F0F0] rounded-[20px] p-6 space-y-3">
        <h4 className="text-body-md font-semibold text-text-primary">What happens after payment?</h4>
        <ul className="space-y-1 text-body-sm text-text-primary-secondary">
          <li>• Your seller dashboard unlocks with menu, orders, and compliance tools.</li>
          <li>• We send a welcome pack with hygiene checklists tailored to your FHRS status.</li>
          <li>• Buyers can discover your shop while you refine your story and menu.</li>
        </ul>
        {willingToImprove === false && (
          <p className="text-body-sm text-red-600">
            Let us know when you’re ready to improve your hygiene rating — we’ll pause onboarding until then.
          </p>
        )}
        {hasActiveSubscription && (
          <p className="text-body-sm text-green-700">
            Subscription active — head to the seller dashboard to finish your launch tasks.
          </p>
        )}
        {errors.commitment && (
          <p className="text-body-sm text-red-600">{errors.commitment}</p>
        )}
      </div>
    </div>
  );
};

export default StepSubscriptionCommitment;
